// SPDX-License-Identifier: BUSL-1.1
/**
 * HMAC-SHA256 primitives for HB-05 signed heartbeats (NocoDB #4406).
 *
 * Pure functions over Web Crypto so the same code runs in Cloudflare
 * Workers (server-side verification) and the api-client (client-side
 * signing) without a Node-only crypto dependency.
 *
 * Canonical signing string is `${timestampSeconds}.${rawBodyText}` —
 * timestamp first so a missing/malformed body still has a length-prefix
 * domain separator, and so the signature changes when the timestamp
 * changes (defeating naive replay even before the freshness window check).
 */
const encoder = new TextEncoder();
/**
 * Generate a fresh random secret. base64url-encoded, 32 bytes of entropy
 * (256 bits — matches HMAC-SHA256 block size). Use for new key creation.
 */
export function generateSigningSecret() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64urlEncode(bytes);
}
/**
 * Generate a fresh kid (key id). Short, URL-safe, opaque to clients.
 * Format: `kid_<12 base64url chars>` (~72 bits of entropy, plenty
 * for a per-agent namespace).
 */
export function generateKid() {
    const bytes = new Uint8Array(9);
    crypto.getRandomValues(bytes);
    return `kid_${base64urlEncode(bytes)}`;
}
/**
 * Compute the HMAC-SHA256 hex signature of `${timestampSeconds}.${body}`
 * using the given base64url-encoded secret.
 */
export async function computeHmacSignature(secret, timestampSeconds, body) {
    const keyBytes = base64urlDecode(secret);
    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const data = encoder.encode(`${timestampSeconds}.${body}`);
    const sig = await crypto.subtle.sign('HMAC', cryptoKey, data);
    return bytesToHex(new Uint8Array(sig));
}
/**
 * Constant-time comparison of two equal-length hex strings. Returns
 * false on length mismatch immediately (length is not secret), then
 * O(n) compare with no early exit (sec-004 family rule).
 */
export function constantTimeEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return diff === 0;
}
// ---------------------------------------------------------------------------
// base64url + hex codec helpers (no external deps)
// ---------------------------------------------------------------------------
function base64urlEncode(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i++)
        bin += String.fromCharCode(bytes[i]);
    // btoa is available in both Workers and modern Node (>=16).
    const b64 = globalThis.btoa(bin);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function base64urlDecode(s) {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = globalThis.atob(padded);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
function bytesToHex(bytes) {
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
}
//# sourceMappingURL=hmac.js.map